// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { STEP_NAME, STEP_NAME_VALUES, isPlantStep } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
} from '../../lib/base';
import { Help } from '../../lib/help';
import { ClearIcon } from '../../lib/icons';

export interface CropStepFormInput {
  // Only used for editing crop steps
  readonly maybeExistingId?: number;
  readonly name: string;
  readonly nameValidation: string;
  readonly timeToStartAfterFirstStep: string;
  readonly timeToStartAfterFirstStepValidation: string;
  readonly description: string;
}

export interface Props {
  readonly disableFields: boolean;
  readonly index: number;
  readonly formKey: number;
  readonly values: CropStepFormInput;
  readonly formsHavePlantStep: boolean;
  readonly onChangeField: (
    index: number,
    fieldName: keyof CropStepFormInput,
    value: string,
    validationFieldName?: 'timeToStartAfterFirstStepValidation' | 'nameValidation',
    validationValue?: string,
  ) => void;
  readonly onRemove: (index: number) => void;
  // readonly onEnter: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export default function NewCropStepForm({
  index,
  disableFields,
  onChangeField,
  values,
  onRemove,
  formsHavePlantStep,
}: Props) {
  const onTimeToStartAfterFirstStepChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangeField(index, 'timeToStartAfterFirstStep', e.target.value);
  };
  // const onChangeName = (e: React.SyntheticEvent<Element, Event>, newInput: string) => {
  //   onChangeField(index, 'name', newInput);
  // };
  const onChangeSelectName = (e: SelectChangeEvent<string>) => {
    onChangeField(index, 'name', e.target.value);
  };
  const onChangeDescription = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangeField(index, 'description', e.target.value);
  };
  const onTimeToStartAfterFirstStepBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    const secondsAsNum = Math.max(0, Number.parseFloat(newVal)).toString();
    const time = secondsAsNum;
    onChangeField(index, 'timeToStartAfterFirstStep', time);
    // This is kind of hacky. Validation work is done in parent component
    onChangeField(index, 'timeToStartAfterFirstStepValidation', time);
  };
  // const onNameBlur = createOnBlurRequired('Name is required', (newVal) =>
  //   onChangeField(index, 'nameValidation', newVal),
  // );
  const isForHarvest = values.name === STEP_NAME.HARVEST;
  const disable = disableFields || isForHarvest;

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ border: '1px solid grey', borderRadius: '4px', p: 2 }}>
      <Grid container flexDirection="row" flexWrap="nowrap" alignItems="center" gap={1}>
        <Grid flexGrow="1">
          <FormControl fullWidth>
            <InputLabel id="step-name-selector">Step Name</InputLabel>
            <Select
              id="step-name-select"
              aria-describedby="step-name-select"
              required
              value={values.name}
              onChange={onChangeSelectName}
              disabled={disable}
              fullWidth
              autoFocus
              label="Step Name"
              // onBlur={onNameBlur}
              // onEnter
            >
              {STEP_NAME_VALUES.filter((name) => (isForHarvest ? true : name !== STEP_NAME.HARVEST))
                .filter((name) =>
                  // Show the plant steps if the currently selected value is a plant step
                  // Or if the other forms don't have a plant step
                  isPlantStep(name) ? isPlantStep(values.name) || !formsHavePlantStep : true,
                )
                .map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid>
          <IconButton disabled={disable} aria-label="remove-crop-step" onClick={() => onRemove(index)}>
            <ClearIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={2}>
        <TextField
          aria-describedby="step-time-to-start-after-plant"
          id="step-time-to-start-after-plant"
          label={
            isForHarvest ? 'Harvest step is automatically set to the total grow time' : 'On Which Day In the Grow Cycle'
          }
          value={values.timeToStartAfterFirstStep}
          onChange={onTimeToStartAfterFirstStepChange}
          onBlur={onTimeToStartAfterFirstStepBlur}
          hasSubtext
          subtext={values.timeToStartAfterFirstStepValidation}
          error={values.timeToStartAfterFirstStepValidation !== ''}
          // onEnter={onEnter}
          type="number"
          numberMin={0}
          required
          inputMode="numeric"
          disabled={disable}
        />
        <Help tooltip="Starting at day 0 in the grow cycle, when should this step take place? It can't be less than 0 or greater than the Total Grow Time set above. When an order is created, Microgreen Manager will create a task for each step, using the order delivery date and When To Start input to determine when the task needs to be done" />
      </Box>
      <TextField
        aria-describedby="step-notes"
        id="step-notes"
        label="Notes"
        value={values.description}
        onChange={onChangeDescription}
        // onEnter={onEnter}
        disabled={disableFields}
      />
    </Box>
  );
}
